import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
import { OriginalDataItem } from './dashboardSlice';
import { Message } from '@mui/icons-material';

interface InitialState {
  isLoading: boolean;
  isLoadingStatus: boolean;
  data: any;
  dataByStatus: any;
  error: any;
}

const initialState: InitialState = {
  isLoading: false,
  isLoadingStatus: false,
  data: [],
  dataByStatus: [],
  error: null
};

const makeModelState = {
  isLoading: false,
  makeList: [],
  modelList: [],
  error: null
};

type ThunkArgs = any;
type ReturnedType = any;

export const analyticsAction = createAsyncThunk<ReturnedType, ThunkArgs>(
  'user/analytics',
  async (params, thunkApi) => {
    const { filter, payload, signal } = params;
    try {
      let URL;
      if (filter === 'Vehicle Number') {
        URL = `${URLs.ANALYTICS}?deviceid=${payload}`;
      } else if (filter === 'Vehicle Make') {
        URL = `${URLs.ANALYTICS}?vehiclemodel=${payload}`;
      } else {
        URL = `${URLs.ANALYTICS}`;
      }
      const data = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      return data?.data?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message,
            severity: 'error'
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

//action for  vehicle make model list
export const getVehicleMakeList = createAsyncThunk<ReturnedType, ThunkArgs>(
  'user/vehiclemake',
  async thunkApi => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.VEHICLE_MAKE}`
      });
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

//slice for vehicle make model list
const vehicleMakeSlice = createSlice({
  name: 'vehiclemake',
  initialState: makeModelState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getVehicleMakeList.pending, state => {
        state.isLoading = true;
        state.makeList = [];
        state.error = null;
      })
      .addCase(getVehicleMakeList.fulfilled, (state, action) => {
        const {
          data: {
            data: { make }
          }
        } = action.payload;
        state.isLoading = false;
        state.makeList = make;
      })
      .addCase(getVehicleMakeList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = null;
      });
  }
});

//action to get vehiclemodel List
export const getVehicleModelList = createAsyncThunk<ReturnedType, ThunkArgs>(
  'user/vehiclemodel',
  async (vehicleId, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.VEHICLE_MAKE}?vehicleMake=${vehicleId}`
      });
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

//slice for vehicle  model list
const vehicleModelSlice = createSlice({
  name: 'vehiclemodel',
  initialState: makeModelState,
  reducers: {
    clearModel: state => {
      state.modelList = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getVehicleModelList.pending, state => {
        state.isLoading = true;
        state.modelList = [];
        state.error = null;
      })
      .addCase(getVehicleModelList.fulfilled, (state, action) => {
        const {
          data: {
            data: { makeModel }
          }
        } = action.payload;
        state.isLoading = false;
        state.modelList = makeModel;
      })
      .addCase(getVehicleModelList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = null;
      });
  }
});

export const analyticsActionByStatus = createAsyncThunk<ReturnedType, ThunkArgs>(
  'user/analytics/status',
  async (params, thunkApi) => {
    try {
      const { vehicle, toast, signal } = params;
      const data = await axiosInstance({
        method: 'GET',
        url: `${URLs.DASHBOARD_VEHICLES_STATUS}${vehicle}`,
        signal: signal
      });
      if (data?.data?.data?.length === 0 && toast) {
        thunkApi.dispatch(
          updateToast({ show: true, message: 'No Vehicle found', severity: 'warning' })
        );
      }
      return data?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(analyticsAction.pending, state => {
        state.isLoading = true;
        state.data = current(state).data;
        state.error = null;
      })
      .addCase(analyticsAction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(analyticsAction.rejected, (state, action) => {
        state.isLoading = false;
        state.data = current(state).data;
        state.error = null;
      })
      .addCase(analyticsActionByStatus.pending, state => {
        state.isLoadingStatus = true;
        state.dataByStatus = current(state).data;
        state.error = null;
      })
      .addCase(analyticsActionByStatus.fulfilled, (state, action) => {
        const data = action.payload;
        state.dataByStatus = data?.map((item: OriginalDataItem) => {
          return {
            id: item?.deviceID,
            type: item?.vehicleDashboardVehicleDetails?.vehicleType,
            lat: item?.latitude,
            lng: item?.longitude,
            status: item?.vehicleDashboardVehicleDetails?.vehicleLiveStatus,
            info: {
              speed: `${item?.vehicleDashboardVehicleDetails?.speed} kmph`,
              todayDistance: `${item?.vehicleDashboardVehicleDetails?.todayDistance?.toFixed(
                2
              )} KM`,
              totalDistance: `${item?.vehicleDashboardVehicleDetails?.totalDistance?.toFixed(
                2
              )} KM`,
              driverName: item?.driverName,
              driverContactNo: item?.contactNumber
            }
          };
        });
        state.isLoadingStatus = false;
        state.error = null;
      })
      .addCase(analyticsActionByStatus.rejected, (state, action) => {
        state.isLoadingStatus = false;
        state.dataByStatus = action.payload;
        state.error = null;
      });
  }
});

export default analyticsSlice.reducer;
export const { clearModel } = vehicleModelSlice.actions;
export const vehicleMakeData = vehicleMakeSlice.reducer;
export const vehicleModelData = vehicleModelSlice.reducer;
